import { useState, FormEvent, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  InputAdornment,
  IconButton,
  Divider,
  LinearProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import NumbersOutlinedIcon from "@mui/icons-material/NumbersOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import { baseUrl } from "../../constants";
import { useNavigate, Link } from "react-router-dom";
import generateFirebaseToken from "../../notifications/firebase-token-generate";

type Strength = "Very Weak" | "Weak" | "Medium" | "Strong" | "Very Strong";

function scorePassword(pw: string): { score: number; label: Strength } {
  let score = 0;
  if (!pw) return { score: 0, label: "Very Weak" };

  // length
  if (pw.length >= 8) score += 20;
  if (pw.length >= 12) score += 20;

  // variety
  if (/[a-z]/.test(pw)) score += 15;
  if (/[A-Z]/.test(pw)) score += 15;
  if (/\d/.test(pw)) score += 15;
  if (/[^A-Za-z0-9]/.test(pw)) score += 15;

  // small penalty for obvious repeats
  if (/([a-zA-Z0-9])\1{2,}/.test(pw)) score -= 10;

  // clamp
  score = Math.max(0, Math.min(100, score));

  let label: Strength = "Very Weak";
  if (score >= 80) label = "Very Strong";
  else if (score >= 60) label = "Strong";
  else if (score >= 40) label = "Medium";
  else if (score >= 20) label = "Weak";
  else label = "Very Weak";

  return { score, label };
}

const SignUp = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // form state
  const [workspaceId, setWorkspaceId] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  // UX state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [capsLockConfirm, setCapsLockConfirm] = useState(false);

  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [error, setError] = useState("");

  // validation
  const wsIdValid = useMemo(
    () => /^\d+$/.test(workspaceId.trim()) && Number(workspaceId) > 0,
    [workspaceId]
  );
  const usernameValid = useMemo(
    () => username.trim().length >= 3,
    [username]
  );
  const passScore = useMemo(() => scorePassword(password), [password]);
  const passwordValid = useMemo(() => passScore.score >= 40, [passScore]);
  const confirmValid = useMemo(() => confirm === password && !!confirm, [confirm, password]);

  const canSubmit = useMemo(
    () => wsIdValid && usernameValid && passwordValid && confirmValid && !loading,
    [wsIdValid, usernameValid, passwordValid, confirmValid, loading]
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    setError("");

    try {
      const payload = {
        username: username.trim(),
        password,
        workspaceId: Number(workspaceId), // server expects number
      };
      const { data } = await axios.post(baseUrl + "/auth/signup", payload);

      const access_token: string = data?.access_token;
      if (!access_token) throw new Error("Invalid server response (no token).");

      localStorage.setItem("accessToken", access_token);
      await generateFirebaseToken();

      navigate("/home");
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Could not sign up. Please try again.";
      setError(Array.isArray(message) ? message[0] : message);
      setSnackbarOpen(true);
      console.error("Sign up error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const strengthColor = useMemo(() => {
    if (passScore.score >= 80) return theme.palette.success.main;
    if (passScore.score >= 60) return theme.palette.success.light;
    if (passScore.score >= 40) return theme.palette.warning.main;
    if (passScore.score >= 20) return theme.palette.warning.light;
    return theme.palette.error.main;
  }, [passScore.score, theme.palette]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "grid",
        placeItems: "center",
        background:
          theme.palette.mode === "dark"
            ? "radial-gradient(1200px 600px at 10% -10%, rgba(93,176,255,0.08), transparent), linear-gradient(180deg, rgba(255,255,255,0.02), transparent)"
            : "radial-gradient(1200px 600px at 10% -10%, rgba(93,176,255,0.20), transparent), linear-gradient(180deg, #F7FAFF, #ECF3FF)",
        px: 2,
      }}
    >
      <Grid container maxWidth="md" alignItems="stretch">
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              p: { xs: 3, sm: 4, md: 6 },
              backdropFilter: "blur(10px)",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(20,22,25,0.6)"
                  : "rgba(255,255,255,0.7)",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)"
              }`,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                mb: 2,
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Typography variant="h4" fontWeight={800} lineHeight={1.2}>
                  Create your account
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Join your workspace and start managing tasks.
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.9 }}>
                v1.0 • quick signup
              </Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Workspace ID"
                    placeholder="e.g. 101"
                    value={workspaceId}
                    onChange={(e) => {
                      // allow only digits
                      const next = e.target.value.replace(/[^\d]/g, "");
                      setWorkspaceId(next);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <NumbersOutlinedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    error={!!workspaceId && !wsIdValid}
                    helperText={
                      !!workspaceId && !wsIdValid
                        ? "Workspace ID must be a positive number."
                        : " "
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Username"
                    placeholder="your.username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineOutlinedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    error={!!username && !usernameValid}
                    helperText={
                      !!username && !usernameValid
                        ? "Username must be at least 3 characters."
                        : " "
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    placeholder="••••••••"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    onKeyUp={(e: any) => {
                      const on = e?.getModifierState?.("CapsLock");
                      setCapsLock(Boolean(on));
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword((p) => !p)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    helperText={
                      capsLock ? (
                        <Box display="inline-flex" alignItems="center" gap={0.5} color="warning.main">
                          <ErrorOutlineOutlinedIcon fontSize="small" /> Caps Lock is on
                        </Box>
                      ) : " "
                    }
                  />

                  {/* Strength meter */}
                  <Box mt={0.5}>
                    <LinearProgress
                      variant="determinate"
                      value={passScore.score}
                      sx={{
                        height: 8,
                        borderRadius: 1,
                        "& .MuiLinearProgress-bar": { backgroundColor: strengthColor },
                      }}
                    />
                    <Box display="flex" justifyContent="space-between" mt={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        Strength:{" "}
                        <Box component="span" sx={{ color: strengthColor, fontWeight: 700 }}>
                          {passScore.label}
                        </Box>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Use 12+ chars, mix Aa, 0-9, and symbols.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    placeholder="••••••••"
                    type={showConfirm ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    onKeyUp={(e: any) => {
                      const on = e?.getModifierState?.("CapsLock");
                      setCapsLockConfirm(Boolean(on));
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowConfirm((p) => !p)}
                            edge="end"
                            size="small"
                          >
                            {showConfirm ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    error={!!confirm && !confirmValid}
                    helperText={
                      capsLockConfirm ? (
                        <Box display="inline-flex" alignItems="center" gap={0.5} color="warning.main">
                          <ErrorOutlineOutlinedIcon fontSize="small" /> Caps Lock is on
                        </Box>
                      ) : !!confirm && !confirmValid
                        ? "Passwords do not match."
                        : " "
                    }
                  />
                </Grid>

                {error ? (
                  <Grid item xs={12}>
                    <Alert severity="error" variant="outlined">
                      {error}
                    </Alert>
                  </Grid>
                ) : null}

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={!canSubmit}
                    startIcon={!loading ? <HowToRegIcon /> : undefined}
                    sx={{
                      py: 1.25,
                      borderRadius: 2,
                      textTransform: "none",
                      fontSize: 16,
                      fontWeight: 700,
                    }}
                  >
                    {loading ? <CircularProgress size={22} /> : "Create Account"}
                  </Button>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    mt={0.5}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Already have an account?
                    </Typography>
                    <Link
                      to="/signIn"
                      style={{
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        textDecoration: "none",
                      }}
                    >
                      Sign in
                    </Link>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Footer */}
            <Box
              mt={4}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
            >
              <Typography variant="caption" color="text.secondary">
                By creating an account, you agree to our{" "}
                <Link
                  to="/terms"
                  style={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    textDecoration: "none",
                  }}
                >
                  Terms
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy"
                  style={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    textDecoration: "none",
                  }}
                >
                  Privacy Policy
                </Link>
                .
              </Typography>
              <Typography variant="caption" color="text.secondary">
                © {new Date().getFullYear()} Your Company. All rights reserved.
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Snackbar for error message */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%", fontSize: "16px", fontWeight: "bold" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SignUp;
